import { useCallback, useEffect, useMemo, useState } from 'react'

export const PAGE_SIZE = 12

/**
 * Pages a list in the client.
 *
 * The API returns whole category arrays with no `page`/`limit` parameters, so
 * paging here is a reveal rather than a fetch: the feed renders a page at a
 * time instead of mounting every card — which, with a live countdown per card,
 * is the difference between one timer and a hundred.
 *
 * When the API grows paging parameters this becomes a `useInfiniteQuery` and
 * nothing above it changes: the shape returned here is the same.
 */
export function usePaginated<T>(items: T[], pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(1)

  // A new list (different category, different search) starts at page one.
  useEffect(() => {
    setPage(1)
  }, [items])

  const visible = useMemo(() => items.slice(0, page * pageSize), [items, page, pageSize])
  const hasMore = visible.length < items.length

  const loadMore = useCallback(() => setPage((current) => current + 1), [])

  return {
    visible,
    hasMore,
    loadMore,
    shownCount: visible.length,
    totalCount: items.length,
  } as const
}
